import {
  createContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useContext
} from "react";
import { getTranslation } from "../redux/slicers/translationSlice";
import { translateJSON } from "src/redux/slicers/translationCombinedSlice";
import { useAppDispatch } from "../redux/Store";
import { useSelector } from "react-redux";
import { selectLanguage } from "src/redux/slicers/userData";
import store from "../redux/Store";

const translateCache: Record<string, Record<string, string>> = { "ROI": { "en": "ROI", "fr": "ROI 2" } };

const TranslationLanguageContext = createContext<string | undefined>(undefined)

interface TranslationLanguageProviderProps {
  language?: string
  children: ReactNode
}

export const TranslationLanguageProvider = ({
  language,
  children
}: TranslationLanguageProviderProps) => (
  <TranslationLanguageContext.Provider value={language}>
    {children}
  </TranslationLanguageContext.Provider>
)

export const useEffectiveLanguage = () => {
  const selectedLanguage = useSelector(selectLanguage)
  const overrideLanguage = useContext(TranslationLanguageContext)
  return overrideLanguage || selectedLanguage || "en"
}

// Custom hook for translation
export const translate = (text: string) => {
  const dispatch = useAppDispatch();
  const selectedLanguage = useEffectiveLanguage()
  const [translatedText, setTranslatedText] = useState<string>(text);

  useEffect(() => {
    const fetchTranslation = async () => {
      if (text in translateCache) {
        setTranslatedText(translateCache[text][selectedLanguage] || text);
        return;
      }
      const response = await fetchTranslate(text, selectedLanguage);
      setTranslatedText(response);
    };

    fetchTranslation();
  }, [dispatch, text, selectedLanguage]);

  return translatedText;
};

export const useTranslate = () => {
  const selectedLanguage = useEffectiveLanguage();
  return useCallback(async (text: string) => {
    if (text in translateCache) {
      return translateCache[text][selectedLanguage] || text
    }
    const response = await fetchTranslate(text, selectedLanguage);
    return response
  }, [selectedLanguage]
  )
};

export const useBulkTanslate = () => {
  const selectedLanguage = useEffectiveLanguage()
  const dispatch = store.dispatch;
  return useCallback(async (jsonText: object) => {
    if (selectedLanguage === "en") {
      return jsonText
    }
    const response = await dispatch<any>(translateJSON({ json: jsonText, targetLanguage: selectedLanguage }))
    return response?.payload
  }, [dispatch, selectedLanguage]
  )
};

const ongoingRequests: Record<string, Promise<string> | undefined> = {};

export const fetchTranslate = async (
  text: string,
  language = ""
): Promise<string> => {
  if (!text || language === "en") {
    return text;
  }

  const requestKey = `${language}::${text}`

  // Check if the translation is already cached
  if (translateCache[text] && translateCache[text][language]) {
    return translateCache[text][language];
  }

  // Check if there is an ongoing request for the same text
  if (ongoingRequests[requestKey]) {
    return ongoingRequests[requestKey] as Promise<string>
  }

  // If no cached or ongoing request, make a new translation request
  const dispatch = store.dispatch;

  // Store the promise in the ongoingRequests object
  ongoingRequests[requestKey] = dispatch<any>(
    getTranslation({ text, targetLanguage: language })
  ).then((result: any) => {
    const translatedText = result?.payload || text;

    // Remove the promise from ongoingRequests after it resolves
    delete ongoingRequests[requestKey]

    return translatedText;
  });

  return ongoingRequests[requestKey] as Promise<string>
};

interface TranslateComponentProps {
  children: string
}

// Functional component, can be used like <TranslateComponent>text</TranslateComponent>
export const TranslateComponent = (props: TranslateComponentProps) => {
  const translatedText = translate(props.children);
  return <>{translatedText}</>;
};

export default translate;
